import { getConsentMode } from '../services/consentMode';
import { type AnalyticsEvent, EventTypes } from './types';

export const COOKIELESS_DISTINCT_ID = '$posthog_cookieless';

const numericProperties = new Set([
    'rw_character_count',
    'rw_message_number',
    'rw_cta_count',
    'rw_field_count',
    'rw_question_count',
    'rw_total_questions',
]);
const booleanProperties = new Set([
    'rw_first_input',
    'rw_filtered_out',
    'rw_has_search_bar',
    'rw_is_minimized',
    'rw_display_allowed',
    'rw_force_display',
    'rw_traffic_control_passed',
    'rw_url_allowed',
    'rw_device_allowed',
    'rw_is_mobile',
    'rw_mobile_enabled',
    'rw_is_cta_page',
    'rw_had_demo_offer',
    'rw_inline_cta_option_enabled',
]);
const enumProperties: Record<string, readonly string[]> = {
    rw_device_type: ['mobile', 'desktop', 'tablet'],
    rw_view_layout: ['compact', 'expanded', 'sidebar', 'fullscreen', 'full', 'minimal'],
    rw_message_source: [
        'user_typed',
        'dynamic_question',
        'follow_up_question',
        'suggested_answer',
        'ai_section',
    ],
    rw_conversation_triggered_by: [
        'user_typed',
        'dynamic_question',
        'follow_up_question',
        'suggested_answer',
        'ai_section',
    ],
};
const measurableEvents = new Set<string>(
    Object.values(EventTypes).filter(
        (type) => !type.startsWith('evaluation_') && type !== EventTypes.CONVERSATION_TRACKED
    )
);
// The per-page-load chat ID from InboundXService; anything else is not sent.
const PRIVATE_SESSION_ID =
    /^session_.*_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Fail closed: never spread raw event/context fields into a private payload. */
export function buildCookielessProperties(
    properties: Record<string, unknown>
): Record<string, unknown> {
    const safe: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(properties)) {
        if (
            numericProperties.has(key) &&
            typeof value === 'number' &&
            Number.isFinite(value) &&
            value >= 0
        )
            safe[key] = value;
        if (booleanProperties.has(key) && typeof value === 'boolean') safe[key] = value;
        if (typeof value === 'string' && enumProperties[key]?.includes(value)) safe[key] = value;
    }
    return safe;
}

/** PostHog's native ingestion sentinel derives a daily server-side anonymous hash.
 * The SDK cannot be initialized here: even memory/cookieless init probes storage.
 * IP and raw UA are used by ingestion then stripped. The only Rose ID sent is the
 * temporary session ID, so the batch job can report conversions per session.
 */
export function captureCookieless(
    apiHost: string | null,
    token: string | null,
    name: string,
    event: AnalyticsEvent,
    properties: Record<string, unknown>
): void {
    if (getConsentMode() === 'full' || !token || !apiHost || !measurableEvents.has(event.type))
        return;
    if (typeof window === 'undefined' || typeof fetch !== 'function') return;
    const hostname = window.location.hostname;
    const { sessionId, environment } = event.context;
    const payload = {
        token,
        event: name,
        uuid: crypto.randomUUID(),
        timestamp: new Date(event.timestamp).toISOString(),
        properties: {
            ...buildCookielessProperties(properties),
            distinct_id: COOKIELESS_DISTINCT_ID,
            $cookieless_mode: true,
            $process_person_profile: false,
            $host: window.location.host,
            $raw_user_agent:
                navigator.userAgent.length > 1000
                    ? `${navigator.userAgent.slice(0, 997)}...`
                    : navigator.userAgent,
            rw_domain: hostname,
            rw_consent_mode: getConsentMode(),
            ...(sessionId && PRIVATE_SESSION_ID.test(sessionId)
                ? { rw_session_id: sessionId }
                : {}),
            ...(environment ? { rw_environment: environment } : {}),
        },
    };
    // No queue, retries, referrer, cookies, local distinct ID, or delayed replay.
    try {
        void fetch(`${apiHost.replace(/\/$/, '')}/i/v0/e/`, {
            method: 'POST',
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify(payload),
        }).catch(() => undefined);
    } catch {
        /* Blocked analytics never prevent chat. */
    }
}
